/**
 * A refusal kept where it happened, on the SOP or on one of its types, until it is dismissed, the next
 * attempt starts, or the reader leaves that place.
 */

import { useCallback, useEffect, useState } from 'react';
import type { Refusal } from './refusal';

interface RefusalPlace {
	sopId: string;
	/** Null for a refusal about the SOP as a whole. */
	typeId: string | null;
}

export interface HeldRefusal {
	refusal: Refusal | null;
	hold: (place: RefusalPlace, refusal: Refusal) => void;
	dismiss: () => void;
}

export const useHeldRefusal = (sopId: string, typeId: string | null): HeldRefusal => {
	const [held, setHeld] = useState<(RefusalPlace & { refusal: Refusal }) | null>(null);
	useEffect(() => setHeld(null), [sopId, typeId]);
	const hold = useCallback((place: RefusalPlace, refusal: Refusal) => setHeld({ ...place, refusal }), []);
	const dismiss = useCallback(() => setHeld(null), []);
	// A refusal that lands after the reader moved on belongs to the place it came from, not this one.
	const here = held !== null && held.sopId === sopId && held.typeId === typeId;
	return { refusal: here ? held.refusal : null, hold, dismiss };
};
